import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { GridColDef } from '@mui/x-data-grid';
import { FilterableDataTable, PageContainer, ConfirmDialog, type RowAction } from '../../components/shared';
import { apiConfig } from '../../api-generated/apiConfig';

const BASE = apiConfig.basePath;

interface UserRow {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  status: string;
  approved: boolean;
  groups: string[];
  createdAt: string;
}

const allUsersColumns: GridColDef[] = [
  { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
  { field: 'firstName', headerName: 'First Name', flex: 0.8, minWidth: 120 },
  { field: 'lastName', headerName: 'Last Name', flex: 0.8, minWidth: 120 },
  {
    field: 'groups',
    headerName: 'Role',
    flex: 0.6,
    minWidth: 100,
    renderCell: (params) => {
      const groups = params.value as string[];
      if (groups.includes('ADMIN')) return <Chip label="Admin" color="error" size="small" />;
      if (groups.includes('USER')) return <Chip label="User" color="primary" size="small" />;
      return <Chip label="None" variant="outlined" size="small" />;
    },
  },
  {
    field: 'enabled',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 100,
    renderCell: (params) =>
      params.value ? (
        <Chip label="Active" color="success" size="small" />
      ) : (
        <Chip label="Disabled" color="default" size="small" />
      ),
  },
  { field: 'createdAt', headerName: 'Joined', flex: 0.8, minWidth: 130,
    renderCell: (params) => {
      if (!params.value) return '—';
      return new Date(params.value as string).toLocaleDateString();
    },
  },
];

const pendingColumns: GridColDef[] = [
  { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
  { field: 'firstName', headerName: 'First Name', flex: 0.8, minWidth: 120 },
  { field: 'lastName', headerName: 'Last Name', flex: 0.8, minWidth: 120 },
  { field: 'createdAt', headerName: 'Signed Up', flex: 0.8, minWidth: 130,
    renderCell: (params) => {
      if (!params.value) return '—';
      return new Date(params.value as string).toLocaleDateString();
    },
  },
];

const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ username: string; action: 'approve' | 'reject' } | null>(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const allUsersQuery = useQuery<UserRow[]>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/admin/users`, { headers: authHeaders() });
      const users = await res.json();
      return users.map((u: any) => ({ ...u, id: u.username }));
    },
  });

  const pendingUsersQuery = useQuery<UserRow[]>({
    queryKey: ['admin', 'users', 'pending'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/admin/users/pending`, { headers: authHeaders() });
      const users = await res.json();
      return users.map((u: any) => ({ ...u, id: u.username }));
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (username: string) => {
      const res = await fetch(`${BASE}/api/admin/users/${username}/approve`, { method: 'POST', headers: authHeaders() });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setConfirmOpen(false);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (username: string) => {
      const res = await fetch(`${BASE}/api/admin/users/${username}/reject`, { method: 'POST', headers: authHeaders() });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setConfirmOpen(false);
    },
  });

  const handleConfirm = useCallback(() => {
    if (!confirmAction) return;
    if (confirmAction.action === 'approve') {
      approveMutation.mutate(confirmAction.username);
    } else {
      rejectMutation.mutate(confirmAction.username);
    }
  }, [confirmAction, approveMutation, rejectMutation]);

  const pendingActions: RowAction<UserRow>[] = [
    {
      label: 'Approve',
      icon: CheckCircleIcon,
      onClick: (row) => { setConfirmAction({ username: row.username, action: 'approve' }); setConfirmOpen(true); },
    },
    {
      label: 'Reject',
      icon: BlockIcon,
      onClick: (row) => { setConfirmAction({ username: row.username, action: 'reject' }); setConfirmOpen(true); },
    },
  ];

  const pendingCount = pendingUsersQuery.data?.length ?? 0;

  return (
    <PageContainer title="User Management" subtitle="Manage accounts and approvals">
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)}>
          <Tab label="All Users" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Pending Approval
                {pendingCount > 0 && (
                  <Chip label={pendingCount} color="warning" size="small" />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <FilterableDataTable<UserRow>
          rows={allUsersQuery.data ?? []}
          columns={allUsersColumns}
          loading={allUsersQuery.isLoading}
          totalCount={allUsersQuery.data?.length ?? 0}
          page={0}
          pageSize={25}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          searchPlaceholder="Search users..."
        />
      )}

      {activeTab === 1 && (
        <FilterableDataTable<UserRow>
          rows={pendingUsersQuery.data ?? []}
          columns={pendingColumns}
          loading={pendingUsersQuery.isLoading}
          totalCount={pendingCount}
          page={0}
          pageSize={25}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          actions={pendingActions}
          searchPlaceholder="Search pending users..."
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title={confirmAction?.action === 'approve' ? 'Approve User' : 'Reject User'}
        message={
          confirmAction?.action === 'approve'
            ? `Approve ${confirmAction?.username}? They will be able to log in and access the system.`
            : `Reject ${confirmAction?.username}? They will not be able to log in.`
        }
        confirmLabel={confirmAction?.action === 'approve' ? 'Approve' : 'Reject'}
        confirmColor={confirmAction?.action === 'approve' ? 'primary' : 'error'}
      />
    </PageContainer>
  );
};

export default UsersPage;
